/** Structure primitives. Typography, spacing and alignment first; a surface only where a boundary clarifies. */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import type { Tone } from '@p1plug/platform';
import { Button, StatusMark, type ButtonProps } from './ds';

export function Section({
  title,
  aside,
  lead,
  children,
  id,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  lead?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section className="p1x-section" id={id}>
      {title ? (
        <div className="p1x-section-head">
          <h2 className="p1x-section-title">{title}</h2>
          {aside ? <div className="p1x-section-aside">{aside}</div> : null}
        </div>
      ) : null}
      {lead ? <p className="p1x-section-lead">{lead}</p> : null}
      {children}
    </section>
  );
}

/** Peer views of one object, as routes. Three to six tabs. */
export function Tabs({ items, label }: { items: { label: string; to: string; end?: boolean }[]; label: string }) {
  return (
    <nav className="p1x-tabs" aria-label={label}>
      {items.map((t) => (
        <NavLink key={t.to} className="p1x-tab" to={t.to} end={t.end}>
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Peer choices that are state, not routes (period, language). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="p1x-segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="p1x-segment"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface Fact {
  term: string;
  value: ReactNode;
  /** IDs, serials and machine values. */
  mono?: boolean;
}

/** Label and value pairs. Empty values read "–" rather than disappearing, so the layout stays stable. */
export function Facts({ items, columns = 1 }: { items: Fact[]; columns?: 1 | 2 | 3 }) {
  return (
    <dl className={`p1x-facts p1x-facts--${columns}`}>
      {items.map((f) => (
        <div key={f.term}>
          <dt>{f.term}</dt>
          <dd className={f.mono ? 'p1-data' : undefined}>
            {f.value === null || f.value === undefined || f.value === '' ? '–' : f.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One figure with its unit and label. Reference information, never the opening of a page. */
export function Metric({
  label,
  value,
  unit,
  note,
  size = 'regular',
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  note?: ReactNode;
  size?: 'regular' | 'large';
}) {
  return (
    <div className={`p1x-metric p1x-metric--${size}`}>
      <span className="p1x-metric-label">{label}</span>
      <span className="p1x-metric-value p1-num">
        {value}
        {unit ? <span className="p1x-metric-unit"> {unit}</span> : null}
      </span>
      {note ? <span className="p1x-metric-note">{note}</span> : null}
    </div>
  );
}

/** A row of metrics separated by rules, not a grid of cards. */
export function MetricRow({ children }: { children: ReactNode }) {
  return <div className="p1x-metric-row">{children}</div>;
}

/** An inline message with a status tone: always icon, title and text. */
export function Notice({
  tone,
  title,
  children,
  actions,
}: {
  tone: Tone;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={`p1x-notice p1-status--${tone}`} role={tone === 'critical' ? 'alert' : 'status'}>
      <span className="p1x-notice-mark">
        <StatusMark tone={tone} size={20} />
      </span>
      <div className="p1x-notice-body">
        <p className="p1x-notice-title">{title}</p>
        {children ? <div className="p1x-notice-text">{children}</div> : null}
        {actions ? <div className="p1x-notice-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/**
 * Empty is a state with meaning. `positive` is for "nothing needs attention";
 * otherwise say what belongs here and the first action.
 */
export function EmptyState({
  title,
  children,
  action,
  positive,
}: {
  title: string;
  children?: ReactNode;
  action?: ButtonProps;
  positive?: boolean;
}) {
  return (
    <div className="p1x-empty">
      {positive ? (
        <span className="p1x-empty-mark p1-status--success">
          <StatusMark tone="success" size={24} />
        </span>
      ) : null}
      <p className="p1x-empty-title">{title}</p>
      {children ? <div className="p1x-empty-text">{children}</div> : null}
      {action ? (
        <div className="p1x-empty-action">
          <Button {...action} />
        </div>
      ) : null}
    </div>
  );
}

/** For a part of the product that has no platform support yet: says what will be here, never shows invented data. */
export function NotConnected({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p1x-pending">
      <p className="p1x-pending-tag">Not available yet</p>
      <p className="p1x-pending-title">{title}</p>
      <div className="p1x-pending-text">{children}</div>
    </div>
  );
}

/** A stable placeholder while data loads: same height as the content, no spinner, no layout shift. */
export function Skeleton({ lines = 3, block }: { lines?: number; block?: boolean }) {
  return (
    <div className="p1x-skeleton" aria-hidden="true">
      {block ? (
        <div className="p1x-skeleton-block" />
      ) : (
        Array.from({ length: lines }, (_, i) => (
          <div key={i} className="p1x-skeleton-line" style={{ width: `${92 - ((i * 23) % 40)}%` }} />
        ))
      )}
    </div>
  );
}

/** A small text tag for classification (never a status: statuses carry an icon). */
export function Tag({ children }: { children: ReactNode }) {
  return <span className="p1x-tag">{children}</span>;
}
